/**
 * Domain services behind the gateway. Their public APIs are being built; these are the collection
 * routes the console's domain shells read (`GET /api/<service>/v1/<collection>`). Records are typed
 * loosely until each service publishes its OpenAPI; shells render id/title/status/time generically.
 */
import type { ApiClient, Service } from './client.ts'
import { itemsOf } from './platform.ts'

export interface DomainRecord {
  id: string
  title?: string
  name?: string
  status?: string
  kind?: string
  created_at?: string
  updated_at?: string
  [k: string]: unknown
}

export interface DomainCollection {
  service: Exclude<Service, 'platform'>
  /** Path under /api/<service>. */
  path: string
  label: string
}

export const DOMAIN_COLLECTIONS = {
  realdata: {
    recordings: { service: 'realdata', path: '/v1/captures', label: 'Recordings' },
    /** Not sent for review: still recording, not (fully) uploaded, or `unusable` (refused when sent; `problem` says
     * why). Nothing here expires. */
    unsubmitted: { service: 'realdata', path: '/v1/admin/captures/unsubmitted', label: 'Not sent' },
    review: { service: 'realdata', path: '/v1/review/queue', label: 'Review queue' },
    datasets: { service: 'realdata', path: '/v1/datasets', label: 'Datasets' },
  },
  realtosim: {
    scans: { service: 'realtosim', path: '/v1/scans', label: 'Scans' },
    scenes: { service: 'realtosim', path: '/v1/scenes', label: 'Scenes' },
    pipeline: { service: 'realtosim', path: '/v1/jobs', label: 'Pipeline' },
  },
  sim: {
    worlds: { service: 'sim', path: '/v1/studio/scenes', label: 'Worlds' },
    tasks: { service: 'sim', path: '/v1/tasks', label: 'Tasks' },
    sessions: { service: 'sim', path: '/v1/sessions', label: 'Sessions' },
    evaluations: { service: 'sim', path: '/v1/learning/evaluations', label: 'Evaluations' },
    engines: { service: 'sim', path: '/v1/engines', label: 'Engines' },
  },
} as const satisfies Record<string, Record<string, DomainCollection>>

export function domainApi(c: ApiClient) {
  return {
    list: async (col: DomainCollection, query?: Record<string, string | number | undefined>) => {
      const r = await c.get<{ items: DomainRecord[]; next?: string | null } | DomainRecord[]>(col.service, col.path, { query })
      return { items: itemsOf(r), next: Array.isArray(r) ? null : (r.next ?? null) }
    },
    get: (service: Exclude<Service, 'platform'>, path: string) => c.get<DomainRecord>(service, path),
    probe: (service: Service, signal?: AbortSignal) => c.probe(service, signal),
  }
}

export type DomainApi = ReturnType<typeof domainApi>
